import {extend, RequestOptionsWithResponse, ResponseError} from 'umi-request';
import {getApiPrefix} from '@/services/paths';
import env from '@/constants/environment';
import {clearCredentials, getAccessToken, getAuthorizationHeader, isExpiredToken, refreshToken} from '@/services/auth';
import storage, {ENGINES} from 'conversional-persistent-storage';
import {isLandingPath} from '@/utils/pathUtils';

// Path prefixes that identify our own backends inside an absolute URL
const OWN_API_PATH_MARKERS = ['/api/v4/', '/partnerApi/'];

export function getEffectiveApiRoot(): string {
  let rootUrl = storage.getItem("rootApiUrl", {}, ENGINES.LOCAL_STORAGE)
  if (rootUrl && typeof rootUrl === 'string' && !rootUrl.startsWith('http')) {
    rootUrl = 'https://' + rootUrl;
  }
  return rootUrl || env.ROOT_URL;
}

export const originalRequest = extend({
  /**
   * Commented this application-level timeout settings because
   * it was preventing the long lasting requests to be made
   * without any further information so it was way too misleading
   * for debugging one issue (#980)
   */
  // timeout: 5000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  getResponse: true,
  // credentials: 'same-origin',
  credentials: 'include',
});

originalRequest.use(async (ctx, next) => {
  if (APP_ENTRY === 'help_center') return await next()
  const {req} = ctx;
  const {url, options} = req;

  /**
   * A multipart body has to declare its own `Content-Type`, boundary included,
   * and only the browser can write that.
   *
   * The client sets `Content-Type: application/json` for every request. On a
   * `FormData` body that is wrong in two different ways depending on how you
   * try to fix it: leave it and the parts are sent as JSON so the server sees no
   * fields; override it with a literal `multipart/form-data` and the `boundary=`
   * parameter is missing, so the body cannot be parsed at all. Setting the
   * header to `null` from a call site does not help either — it is serialised as
   * the string `"null"`.
   *
   * Deleting it here is the only place that works: interceptors run *after* the
   * instance defaults have been merged, so this is the first point at which the
   * inherited header can actually be removed. `fetch` then derives it from the
   * body.
   */
  if (typeof FormData !== 'undefined' && options.data instanceof FormData) {
    const headers = (ctx.req.options.headers ?? {}) as Record<string, string>;
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'content-type') delete headers[key];
    }
    ctx.req.options.headers = headers;
  }
  if (options.thirdPartyCall) {
    ctx.req.url = url
    ctx.req.options.credentials = 'omit'
    return await next()
  }

  if (!(options.absoluteURL || url.startsWith("http"))) {
    // Dynamically determine the correct API prefix based on the endpoint + method
    const apiPrefix = getApiPrefix(url, options.method);

    if (url.indexOf(apiPrefix) !== 0) {
      ctx.req.url = apiPrefix + ctx.req.url;
    }
    ctx.req.url = getEffectiveApiRoot() + ctx.req.url;
  } else {
    // Absolute links INTO OUR OWN API (pagination links.next etc.) are
    // re-based onto the configured root instead of trusted verbatim — the
    // server behind the proxy may emit the wrong scheme/host (e.g. http://
    // behind the local https proxy), and they must honor rootApiUrl.
    const marker = OWN_API_PATH_MARKERS.find((prefix) => url.includes(prefix));
    if (marker) {
      ctx.req.url = getEffectiveApiRoot() + url.slice(url.indexOf(marker));
    }
  }
  if (!options.noAuthHeader) {
    ctx.req.options.headers = {
      ...(ctx.req.options.headers || {}),
      ...getAuthorizationHeader()
    }
  }

  // ctx.req.options = {
  //   ...options,
  //   journeyId: 'vr4Qm48A',
  // };
  await next();
});

type IRequestOptions = {
  getResponse?: boolean,
  absoluteURL?: string,
  thirdPartyCall?: boolean,
  method: string,
  data?: Record<string, unknown> | FormData,
  /**
   * Per-request header overrides, merged over the client's defaults by
   * umi-request.
   *
   * Needed for multipart uploads: the client sets a global
   * `Content-Type: application/json`, which a `FormData` body cannot override on
   * its own — the request goes out as JSON with an empty body and the server
   * sees no file. Naming `multipart/form-data` here is what lets the boundary
   * be written correctly.
   */
  headers?: Record<string, string>,
  params?: Record<string, unknown>,
  noAuthHeader?: boolean,
  queryString?: Record<string, string>,
  signal?: AbortSignal,
}
export function request<T>(url: string, options: IRequestOptions): Promise<unknown> {
  return new Promise((resolve) => {
    requestHandler(url, options, resolve)
  })
}

/** This function is written in order to inject the resolve function as a dependency. */
function requestHandler(url: string, options: IRequestOptions, resolve: (value: unknown) => void) {
  /**
   * An authenticated call built without a token is guaranteed to 401, and a 401
   * that isn't literally "Expired Token" clears credentials and bounces the user
   * to /user/login (see handleRedirectToLogin). So a single request that missed
   * the token logs the user out of a page whose every other request succeeded —
   * observed on /organization/users, where the third of three identical
   * sequential `fetchAssignees` calls goes out bare while the first two carry
   * the header.
   *
   * Marking it here (rather than in the interceptor, whose `ctx.req.options` is
   * a clone that never reaches errorHandler) keeps the session-teardown decision
   * keyed on a token we actually sent and were rejected for. This does not
   * explain why the token reads back empty; it stops that gap escalating into a
   * logout.
   */
  if (!options.noAuthHeader && !getAuthorizationHeader().Authorization) {
    // eslint-disable-next-line no-console
    console.warn(`[auth] sending ${url} without an Authorization header — stored token read back empty`)
    ;(options as IRequestOptions & { authHeaderMissing?: boolean }).authHeaderMissing = true
  }

  originalRequest(url, options).then((response) => {
    resolve(response)
  }).catch((error) => {
    errorHandler(error, url, options as RequestOptionsWithResponse, resolve)
  })
}

// region Error Handling


let isRefreshing = false;
/**
 * Putting a request in queue is for that when we find out that our access token is expired and not valid anymore
 * we tend to do the refreshing process. Meanwhile some requests will be made. Those requests would not successfully responded
 * because we have not got the new access token yet. So we check it out every 100ms if we have finished our refresh token process.
 * If it was not finished yet, the request will be back in the queue. In case refreshing the token is done, we will resend the request.
 * The important fact is that we keep the `resolve` function so that our component/redux action will get to know when the promise is
 * successfully resolved.
 * */
function putRequestInQueue(url: string, options: RequestOptionsWithResponse, resolve: (value: unknown) => void) {
  setTimeout(() => {
    isRefreshing ? putRequestInQueue(url, options, resolve) : requestHandler(url, options as IRequestOptions, resolve)
  }, 100)
}

function handleRedirectToLogin() {
  clearCredentials()
  if (location.pathname?.includes('/user/')) return
  // Never carry a redirect-only path over: after login it would resolve to a
  // landing page that sends the user straight back here.
  location.replace(isLandingPath(location.pathname) ? `/user/login?redirect=${location.pathname}` : '/user/login')
}


/**
 * When access token expires, we tend to refresh the access token using our refresh token
 * While the token is being refreshed, every other request goes into a queue-like concept
 * Every queue member checks itself every 100ms so that if the token is refreshed, we retry the request
 * The control flow of resolving the promise is kept carefully so that user do not face any lag/glitch
 * */
async function handleTokenRefreshing(error: ResponseError, url: string, options: RequestOptionsWithResponse, resolve: (value: unknown) => void) {
  if (isRefreshing) {
    return putRequestInQueue(url, options, resolve)
  }
  isRefreshing = true
  await refreshToken()
  isRefreshing = false
  return requestHandler(url, options as IRequestOptions, resolve)
}

export async function handleUnauthorizedError(error: ResponseError, url: string, options: RequestOptionsWithResponse, resolve: (value: unknown) => void) {
  if (isExpiredToken(error)) return await handleTokenRefreshing(error, url, options, resolve)

  /**
   * This call went out with no token, so its 401 says nothing about whether the
   * stored session is valid — but only skip the teardown if a token is readable
   * *now*. That distinguishes "the session is fine and this one request missed
   * it" (suppress, the caller renders its own empty state) from "there is no
   * session at all" (fall through and send the user to login, otherwise every
   * request 401s onto a blank page).
   */
  if (options?.['authHeaderMissing'] && getAccessToken()) return

  return handleRedirectToLogin()
}

export async function errorHandler(error: ResponseError, url: string, options: RequestOptionsWithResponse, resolve: (value: unknown) => void) {
  const {response} = error
  if (response?.status === 401) {
    await handleUnauthorizedError(error, url, options, resolve)
  }
  if (!isRefreshing)
    resolve({
      response,
      data: error.data
    })
}
// endregion
